<template>
  <div class="report-card-viewer">
    <v-dialog v-model="dialog" persistent scrollable max-width="520px">
      <v-card class="profile-dialog-card report-card-year-dialog">
        <div class="profile-dialog-header report-card-year-dialog__header">
          <div class="report-card-year-dialog__title">
            <v-avatar size="44" color="green lighten-5">
              <v-icon color="primary">mdi-file-document-outline</v-icon>
            </v-avatar>
            <div>
              <span>Report cards</span>
              <h2>View report card</h2>
              <p>Select an academic year for {{ studentDisplayName }}.</p>
            </div>
          </div>
          <v-btn icon aria-label="Close report card dialog" @click.stop="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider />

        <v-card-text class="profile-dialog-body report-card-year-dialog__body">
          <section class="report-card-year-dialog__section">
            <div class="report-card-year-dialog__section-heading">
              <span>
                <v-icon size="19" color="primary">mdi-calendar-range</v-icon>
              </span>
              <div>
                <h3>Academic year</h3>
                <p>Choose the year you want to review.</p>
              </div>
            </div>

            <v-select
              v-model="selectedYear"
              :items="years"
              :loading="loading"
              :disabled="loading"
              :error-messages="yearErrors"
              :hide-details="yearErrors.length === 0"
              return-object
              item-text="name"
              outlined
              dense
              label="Select academic year"
              prepend-inner-icon="mdi-calendar-outline"
              no-data-text="No academic years available"
            />

            <div v-if="!loading && !years.length" class="report-card-year-dialog__empty">
              No academic years are available for this student.
            </div>
          </section>
        </v-card-text>

        <v-divider />

        <v-card-actions class="profile-dialog-actions report-card-year-dialog__actions">
          <v-btn text @click="close">Cancel</v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="loading"
            :disabled="!selectedYear"
            @click="submit()"
          >
            <v-icon left small>mdi-eye-outline</v-icon>
            View report card
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="showPdf"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      content-class="report-preview-dialog"
    >
      <v-card class="report-preview">
        <header class="report-preview__header">
          <div class="report-preview__identity">
            <span class="report-preview__icon">
              <v-icon color="primary">mdi-file-pdf-box</v-icon>
            </span>
            <div class="report-preview__heading">
              <span>Report cards</span>
              <h2>Student report card</h2>
              <p>{{ reportPreviewContext }}</p>
            </div>
          </div>
          <v-btn
            icon
            class="report-preview__close"
            aria-label="Close student report card preview"
            @click.stop.prevent="close"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>
        <main class="report-preview__document">
          <embed
            id="embedPDF"
            :src="pdf"
            type="application/pdf"
            title="Student report card PDF"
          />
        </main>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import yearService from "@/modules/ReportCard/Year/service.js";
import classRoomService from "@/modules/ClassRoom/service.js";
import gradeService from "@/modules/ReportCard/Grades/service.js";
import termService from "@/modules/ReportCard/Terms/service.js";
import termCommentService from "@/modules/ReportCard/TermComments/service.js";
import subjectService from "@/modules/ReportCard/Subject/service.js";
import studentService from "@/modules/Student/service.js";
const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");
import pdfMake from "pdfmake/build/pdfmake.min";
import { pdfMake as pdfFonts } from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.vfs;
export default {
  name: "generate-reportcard",
  props: {
    Dialog: {
      default: false,
    },
    Student: {
      type: Object,
    },
  },
  data() {
    return {
      pdfBlobUrl: null,
      pdfFileName: null,
      years: [],
      loading: false,
      dialog: false,
      YearId: null,
      selectedYear: null,
      doc: {
        content: [],
      },
      classroom: [],
      students: [],
      grades: [],
      subjects: [],
      pdf: null,
      terms: [],
      termComment: [],
      classPosition: [],
      showPdf: false,
      reportCard: [],
      termCommentTable: [],
    };
  },
  mixins: [validationMixin],
  validations: {
    YearId: {
      required,
    },
  },
  watch: {
    Dialog(val) {
      this.dialog = val;
      if (val) {
        this.getYear();
      }
    },
    selectedYear(val) {
      if (val) this.YearId = val.id;
    },
  },
  beforeDestroy() {
    this.clearPdfUrl();
  },
  computed: {
    ...mapGetters("global", ["currentBusiness"]),
    studentDisplayName() {
      if (!this.Student) return "this student";
      const name = [this.Student.firstName, this.Student.lastName]
        .filter(Boolean)
        .join(" ");
      return name || this.Student.name || "this student";
    },
    reportPreviewContext() {
      const year = this.selectedYear && this.selectedYear.name;
      return [this.studentDisplayName, year].filter(Boolean).join(" · ");
    },
    yearErrors() {
      const errors = [];
      if (!this.$v.YearId.$dirty) return errors;
      if (!this.$v.YearId.required)
        errors.push(this.$t("validations.fieldIsRequired"));

      return errors;
    },
  },
  methods: {
    clearPdfUrl() {
      if (this.pdfBlobUrl) {
        URL.revokeObjectURL(this.pdfBlobUrl);
        this.pdfBlobUrl = null;
      }
      this.pdf = null;
    },
    async getLogoBase64() {
      if (!this.currentBusiness?.imageUrl) return null;

      const response = await fetch(this.currentBusiness.imageUrl, {
        mode: "cors",
      });
      const blob = await response.blob();

      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    },
    capitalizedText(val) {
      if (val)
        return val
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      return null;
    },
    getYear() {
      this.loading = true;
      if (
        this.Student &&
        this.Student.ClassStudents &&
        this.Student.ClassStudents.length
      ) {
        let filteredClassIds = [];
        this.Student.ClassStudents.map((classes) => {
          filteredClassIds.push(classes.ClassId);
        });
        yearService
          .getAll({ filteredClasses: filteredClassIds })
          .then((response) => {
            this.loading = false;
            this.years = response.data;
          })
          .catch((error) => {
            this.loading = false;
            console.log("error getting year", error);
          });
      }
    },
    close() {
      this.empty();
      this.$emit("close");
    },
    empty() {
      this.clearPdfUrl();
      this.YearId = null;
      this.dialog = false;
      (this.years = []),
        (this.loading = false),
        (this.selectedYear = null),
        (this.doc = {
          content: [],
        }),
        (this.classroom = []),
        (this.grades = []),
        (this.subjects = []),
        (this.terms = []),
        (this.showPdf = false),
        (this.reportCard = []),
        (this.termComment = []),
        (this.students = []),
        (this.classPosition = []),
        (this.termCommentTable = []);
      this.$v.$reset();
    },
    getStudents() {
      return studentService
        .getStudentByClassRoomId({
          ClassRoomId: this.Student.ClassRoomStudents[0].ClassRoomId,
        })
        .then((response) => {
          console.log("getStudents response", response);
          this.students = response.data;
        })
        .catch((error) => {
          console.log("error getting students", error);
        });
    },
    getClassroom() {
      return classRoomService
        .getById(this.Student.ClassRoomStudents[0].ClassRoomId)
        .then((response) => {
          this.classroom = response.data;
        })
        .catch((error) => {
          console.log("error getting classRooms", error);
        });
    },
    getSubjects() {
      if (
        this.classroom.Curriculum &&
        this.classroom.Curriculum.subjectMeta &&
        this.classroom.Curriculum.subjectMeta.subjects
      ) {
        let subjectId = [];
        this.classroom.Curriculum.subjectMeta.subjects.map((sub) => {
          subjectId.push(sub.id);
        });
        return subjectService
          .getAll({ filteredSubjects: subjectId })
          .then((response) => {
            this.subjects = response.data;
            if (this.subjects) {
              this.classroom.Curriculum.subjectMeta.subjects.forEach((sub) => {
                this.subjects.some((row) => {
                  if (row.id === sub.id) {
                    row.weightage = sub.marks;
                  }
                });
              });
            }
          })
          .catch((error) => {
            console.log("error getting subjects", error);
          });
      }
    },
    getTerms() {
      return termService
        .getAll({
          YearId: this.YearId,
        })
        .then((response) => {
          this.terms = response.data;
        })
        .catch((error) => {
          console.log("error getting terms", error);
        });
    },
    getGrades() {
      let studentIds = [];
      this.students.map((row) => {
        studentIds.push(row.StudentId);
      });
      return gradeService
        .getAll({
          YearId: this.YearId,
          filteredStudents: studentIds,
          ClassroomId: this.classroom.id,
        })
        .then((response) => {
          console.log("grade response", response);
          this.grades = response.data;
        })
        .catch((error) => {
          console.log("error getting grade", error);
        });
    },
    getTermComments() {
      if (this.terms.length) {
        let termsId = [];
        this.terms.map((term) => {
          termsId.push(term.id);
        });
        return termCommentService
          .getAll({
            filteredTerms: termsId,
            StudentId: this.Student.id,
          })
          .then((response) => {
            this.termComment = response.data;
            if (this.termComment) {
              let commentRows = [];
              this.termCommentTable = [];
              this.termComment.forEach((row) => {
                commentRows = [];
                commentRows.push({
                  text: `${this.capitalizedText(row.Term.name)} Comments: `,
                  bold: true,
                  margin: [4, 10, 4, 10],
                });
                commentRows.push({
                  text: `${this.capitalizedText(row.comment)}`,
                  margin: [4, 10, 4, 10],
                });
                this.termCommentTable.push(commentRows);
              });
            }
          })
          .catch((error) => {
            console.log("error getting term comments", error);
          });
      }
    },
    mergeArrays() {
      this.reportCard = [];
      if (this.subjects && this.terms) {
        const headerRow = [{ text: "Subject", bold: true }];
        const secondRow = [{ text: "Date", bold: true }];
        const ThirdRow = [{ text: "", bold: true }];
        const totalTermObtainedMarks = Array.from(
          { length: this.terms.length },
          () => 0
        );
        const totalTermPercentageMarks = Array.from(
          { length: this.terms.length },
          () => 0
        );
        this.terms.forEach((term) => {
          headerRow.push({
            text: `${this.capitalizedText(term.name)} Term`,
            bold: true,
            colSpan: 2,
            alignment: "center",
          });
          headerRow.push({});
          secondRow.push({
            text: this.$moment(term.createdAt).format("MM/DD/YYYY"),
            bold: true,
            alignment: "center",
            colSpan: 2,
          });
          secondRow.push({});
          ThirdRow.push({ text: "Marks", bold: true, alignment: "center" });
          ThirdRow.push({ text: "%", bold: true, alignment: "center" });
        });
        this.reportCard.push(headerRow);
        this.reportCard.push(secondRow);
        this.reportCard.push(ThirdRow);
        this.subjects.forEach((subject) => {
          const gradeRows = [
            { text: `${subject.name}(${subject.weightage}%)` },
          ];

          this.terms.forEach((term, index) => {
            const grade = this.grades.find(
              (g) =>
                g.SubjectId === subject.id &&
                g.TermId === term.id &&
                g.StudentId === this.Student.id
            );
            if (grade) {
              gradeRows.push({
                text: `${parseFloat(grade.ObtainedMarks).toFixed(1)}`,
                alignment: "center",
              });
              totalTermObtainedMarks[index] += parseFloat(grade.ObtainedMarks);
            } else {
              gradeRows.push({ text: "", alignment: "center" });
            }
            const weightedMarks = grade
              ? (parseFloat(grade.ObtainedMarks) /
                  parseFloat(grade.totalMarks)) *
                parseFloat(subject.weightage)
              : 0;
            gradeRows.push({
              text: `${weightedMarks.toFixed(1)}%`,
              alignment: "center",
            });
            totalTermPercentageMarks[index] += parseFloat(weightedMarks);
          });
          this.reportCard.push(gradeRows);
        });
        const totalRow = [{ text: "Total", bold: true }];
        for (let i = 0; i < totalTermObtainedMarks.length; i++) {
          if (totalTermObtainedMarks[i])
            totalRow.push({
              text: `${totalTermObtainedMarks[i].toFixed(1)}`,
              bold: true,
              alignment: "center",
            });
          else totalRow.push({ text: "", bold: true, alignment: "center" });
          totalRow.push({
            text: `${totalTermPercentageMarks[i].toFixed(1)}%`,
            bold: true,
            alignment: "center",
          });
        }
        this.reportCard.push(totalRow);
        const PositionRow = [{ text: "Class Position", bold: true }];
        this.classPosition.map((position) => {
          PositionRow.push({
            text: position,
            bold: true,
            alignment: "center",
            colSpan: 2,
          });
          PositionRow.push({});
        });
        this.reportCard.push(PositionRow);
      }
    },
    formatOrdinal(num) {
      if (!num) return "";

      const n = parseInt(num, 10);
      const mod100 = n % 100;

      if (mod100 >= 11 && mod100 <= 13) {
        return `${n}th`;
      }

      switch (n % 10) {
        case 1:
          return `${n}st`;
        case 2:
          return `${n}nd`;
        case 3:
          return `${n}rd`;
        default:
          return `${n}th`;
      }
    },
    findRank() {
      this.classPosition = []; // reset first

      this.terms.forEach((term) => {
        const results = this.students.map((stu) => {
          const totalMarks = this.grades
            .filter(
              (g) => g.StudentId === stu.StudentId && g.TermId === term.id
            )
            .reduce((sum, g) => {
              const subject = this.subjects.find((s) => s.id === g.SubjectId);

              if (!subject) return sum;

              const weighted =
                (Number(g.ObtainedMarks) / Number(g.totalMarks)) *
                Number(subject.weightage);

              return sum + weighted;
            }, 0);

          return {
            StudentId: stu.StudentId,
            totalMarks,
          };
        });

        results.sort((a, b) => b.totalMarks - a.totalMarks);

        const rankIndex = results.findIndex(
          (r) => r.StudentId === this.Student.StudentId
        );

        const rank = rankIndex >= 0 ? rankIndex + 1 : null;

        this.classPosition.push(this.formatOrdinal(rank));
      });
    },
    async submit() {
      try {
        this.loading = true;
        const studentId = this.Student.id || this.Student.StudentId;
        const response = await classRoomService.getSavedStudentReport({
          StudentId: studentId,
          YearId: this.YearId,
        });
        const savedReport =
          (response && response.data && response.data.data) ||
          (response && response.data) ||
          null;
        const docDefinition =
          savedReport &&
          savedReport.snapshotMeta &&
          savedReport.snapshotMeta.docDefinition;

        if (!docDefinition) {
          throw new Error(
            "No saved report card is available for this student and year. Force regenerate the classroom report card, then try again."
          );
        }

        this.clearPdfUrl();
        this.pdfFileName = this.getReportCardFileName();
        pdfMake.createPdf(docDefinition).getBlob((blob) => {
          this.pdfBlobUrl = URL.createObjectURL(blob);
          this.pdf = this.pdfBlobUrl;
          this.dialog = false;
          this.showPdf = true;
          this.loading = false;
        });
      } catch (error) {
        this.loading = false;
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 5000,
          })
          .fire({
            icon: "error",
            title:
              (error &&
                error.response &&
                error.response.data &&
                error.response.data.message) ||
              error.message ||
              "Unable to load the saved report card",
          });
      }
    },
    async createDocument() {
      this.doc.content = [];

      this.doc.content.push({
        text: "REPORT CARD",
        alignment: "center",
        fontSize: 22,
        bold: true,
        margin: [0, 0, 0, 12],
      });

      // ===== Header =====
      this.doc.content.push({
        columns: [
          {
            image: await this.getLogoBase64(),
            width: 60,
            alignment: "left",
          },

          {
            stack: [
              { text: this.currentBusiness.name, bold: true, fontSize: 18 },
              {
                text: `${this.currentBusiness.address}, ${this.currentBusiness.state} ${this.currentBusiness.zip}`,
                fontSize: 10,
                margin: [0, 2, 0, 0],
              },
              { text: this.currentBusiness.phoneNumber, fontSize: 10 },
              { text: this.currentBusiness.email, fontSize: 10 },
            ],
            width: "*",
            alignment: "right", // optional, keeps text on the right side
          },
        ],
        margin: [0, 0, 0, 16],
      });
      // ===== Student Info =====
      this.doc.content.push({
        columns: [
          {
            text: `Name: ${this.Student.fullName}`,
            fontSize: 11,
          },
          {
            text: `Year: ${this.selectedYear.name}`,
            alignment: "center",
            fontSize: 11,
          },
          {
            text: this.reportCard.length
              ? `Class Position: ${
                  this.classPosition[this.classPosition.length - 1]
                }`
              : "",
            alignment: "right",
            fontSize: 11,
          },
        ],
        margin: [0, 0, 0, 10],
      });

      // ===== Optional Year Note =====
      if (this.selectedYear.note) {
        this.doc.content.push({
          text: this.selectedYear.note,
          italics: true,
          fontSize: 10,
          margin: [0, 0, 0, 10],
        });
      }

      // ===== Grades Table =====
      if (this.subjects && this.terms) {
        const borderStyle = {
          hLineWidth(i, node) {
            return i === 0 || i === node.table.body.length ? 2 : 1;
          },
          vLineWidth(i, node) {
            return i === 0 || i === node.table.widths.length ? 2 : 1;
          },
          hLineColor() {
            return "#000";
          },
          vLineColor() {
            return "#000";
          },
        };

        this.doc.content.push({
          table: {
            widths: "*",
            body: this.reportCard,
          },
          layout: borderStyle,
          margin: [0, 0, 0, 12],
        });
      }

      // ===== Term Comments =====
      if (this.termComment.length) {
        this.doc.content.push({
          table: {
            widths: "*",
            body: this.termCommentTable,
          },
          layout: "noBorders",
          margin: [0, 12, 0, 0],
        });
      }

      // pdfMake.createPdf(this.doc).getDataUrl((data) => {
      //   this.pdf = data;
      // });

      const fileName = this.getReportCardFileName();

      pdfMake.createPdf(this.doc).getBlob((blob) => {
        this.pdfFileName = fileName;
        this.pdfBlobUrl = URL.createObjectURL(blob);
        this.pdf = this.pdfBlobUrl;
      });

      this.dialog = false;
      this.showPdf = true;
    },
    getReportCardFileName() {
      const student = this.Student.fullName
        .replace(/\s+/g, "_")
        .replace(/[^\w]/g, "");

      const year = this.selectedYear.name.replace(/\s+/g, "-");

      return `${student}_Report_Card_${year}.pdf`;
    },
  },
};
</script>

<style scoped>
.report-card-year-dialog__header,
.report-card-year-dialog__title,
.report-card-year-dialog__section-heading {
  display: flex;
  align-items: center;
}

.report-card-year-dialog__header {
  justify-content: space-between;
  background: linear-gradient(135deg, #f7fbf8 0%, #edf7ef 100%);
}

.report-card-year-dialog__title {
  min-width: 0;
  gap: 12px;
}

.report-card-year-dialog__title > div {
  min-width: 0;
}

.report-card-year-dialog__title span {
  color: #2f7d4a;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.report-card-year-dialog__title h2 {
  margin: 1px 0 0;
  color: #253047;
  font-size: 18px;
  line-height: 1.25;
}

.report-card-year-dialog__title p,
.report-card-year-dialog__section-heading p {
  margin: 2px 0 0;
  color: #7f8c82;
  font-size: 10px;
}

.report-card-year-dialog__body {
  background: #f8faf8;
}

.report-card-year-dialog__section {
  padding: 17px;
  border: 1px solid #e2e9e3;
  border-radius: 12px;
  background: #fff;
}

.report-card-year-dialog__section-heading {
  gap: 10px;
  margin-bottom: 18px;
}

.report-card-year-dialog__section-heading > span {
  display: flex;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #edf7ef;
}

.report-card-year-dialog__section-heading h3 {
  margin: 0;
  color: #293448;
  font-size: 14px;
}

.report-card-year-dialog__section ::v-deep .v-text-field--outlined .v-input__slot {
  border-radius: 9px;
}

.report-card-year-dialog__empty {
  margin-top: 12px;
  padding: 12px;
  border: 1px dashed #dce4dd;
  border-radius: 9px;
  color: #879189;
  background: #fafcfb;
  font-size: 11px;
  text-align: center;
}

.report-card-year-dialog__actions {
  background: #fff;
}

@media (max-width: 599px) {
  .report-card-year-dialog__header {
    min-height: 70px;
    padding: 13px 14px;
  }

  .report-card-year-dialog__title p {
    display: none;
  }

  .report-card-year-dialog__body {
    padding: 14px !important;
  }

  .report-card-year-dialog__section {
    padding: 14px;
  }
}
</style>
